<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { useSettingsStore } from '@/stores/SettingsStore.ts';
import { nextTick, onMounted, reactive, watch } from 'vue';
import { RadiationIcon } from 'lucide-vue-next';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { type LoggingLevel, LoggingLevels } from '@/types/LoggingLevels.ts';
import { setLoggingLevelAction } from '@/actions/app/SetLoggingLevelAction.ts';
import { eventEmitter } from '@/lib/eventEmitter.ts';
import { fullResetAction } from '@/actions/app/FullResetAction.ts';
import { showAlertDialogAsync } from '@/wrappers/ShowAlertDialogAsync.ts';

const settingsStore = useSettingsStore();

interface ComponentState {
    loggingLevelCopy: LoggingLevel;
    changesMade: boolean;
}

const state: ComponentState = reactive({
    loggingLevelCopy: 'info',
    changesMade: false,
});

const fullReset = async () => {
    const userConfirmed = await showAlertDialogAsync({
        title: `Are you sure you want to reset all application data? You will be logged out after.`,
        actionText: `Reset everything`,
    });

    if (userConfirmed) {
        await fullResetAction();
        eventEmitter.emit('setSettingsDialogVisibility', { visible: false });
    }
};

const save = () => {
    settingsStore.loggingLevel = state.loggingLevelCopy;
    state.changesMade = false;
    setLoggingLevelAction(state.loggingLevelCopy);
    eventEmitter.emit('setSettingsDialogVisibility', {
        visible: false,
    });
};

onMounted(async () => {
    state.loggingLevelCopy = settingsStore.loggingLevel;
    await nextTick();
    state.changesMade = false;
});

watch(
    () => [state.loggingLevelCopy],
    () => {
        state.changesMade = true;
    },
);
</script>

<template>
    <main class="flex-shrink flex-grow p-4 pt-1">
        <section class="mb-8">
            <h2 class="mb-4 border-b pb-2">Debug logging</h2>
            <Select v-model="state.loggingLevelCopy">
                <SelectTrigger class="w-full">
                    <SelectValue placeholder="Select a model" />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem
                        v-for="loggingLevel in LoggingLevels"
                        :value="loggingLevel"
                        :key="loggingLevel"
                    >
                        {{ loggingLevel }}
                    </SelectItem>
                </SelectContent>
            </Select>
        </section>
        <section>
            <h2 class="mb-4 border-b pb-2">Full reset</h2>
            <Button variant="destructive" @click="fullReset">
                <RadiationIcon />
                Reset all application data
            </Button>
        </section>
    </main>

    <footer class="mt-4 flex flex-shrink-0 justify-end border-t p-4">
        <Button variant="success" @click="save" :disabled="!state.changesMade"
            >Save and close</Button
        >
    </footer>
</template>
